import { Children, cloneElement, isValidElement, useId, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Cpu,
  Info,
  Loader2,
  Rocket,
  Sparkle,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDeploymentGuard } from "../useDeploymentGuard";
import { LegacyLink } from "@/components/layout/LegacyLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useProjects } from "@/contexts/ProjectContext";
import { isAdmin } from "../permissions";
import { formFromPreset, initialForm, recipePayload, resourcesFor, slug } from "../recipes";
import { Label } from "@/components/ui/label";
import Tooltip from "@mui/material/Tooltip";
import { Textarea } from "@/components/ui/textarea";
import { fieldControlClass } from "@/components/ui/field-styles";
import { InfoTip } from "./InfoTip";
import { CatalogTargetFields, validCatalogTarget } from "./CatalogTargetFields";
import { cn } from "@/utils/cn";
import { omnimizerApi, omnimizerKeys } from "../api";
import {
  GPU_CATALOG,
  HOT_SWITCH_GPU_MEMORY_UTILIZATION,
  MODEL_CATALOG,
  REASONING_PARSERS,
  TOOL_CALL_PARSERS,
  buildDeploymentPayload,
  customModel,
  defaultEngine,
  estimateSizing,
  gpuSpec,
  type CatalogModel,
  type DeploymentForm,
  type Sizing,
} from "../catalog";
import { saveCatalogIntent } from "../catalog-sync";
import type { PredefinedDeployment } from "../types";

const STEPS = ["model", "gpus", "configuration", "review"] as const;
const CONTEXT_PRESETS = [2048, 4096, 8192, 16384, 32768, 131072];
const GPU_COUNTS = [1, 2, 4, 8];
const fieldClass = fieldControlClass + " h-10 px-3";

function Field({ label, hint, tooltip, htmlFor, children }: { label: string; hint?: string; tooltip?: string; htmlFor?: string; children: ReactNode }) {
  const { t } = useTranslation();
  const hintId = `${useId()}-hint`;
  return (
    <div className="flex min-w-0 flex-col gap-1.5 [&>input]:box-border [&>input]:max-w-full [&>select]:box-border [&>select]:max-w-full">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={htmlFor}>{label}</Label>
        {tooltip && <Tooltip title={tooltip} describeChild arrow enterTouchDelay={0}>
          <button type="button" aria-label={t("omnimizer.wizard.guidance.moreInfo", { field: label })}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Info className="size-4" aria-hidden="true" />
          </button>
        </Tooltip>}
      </div>
      {tooltip && hint ? Children.map(children, child =>
        isValidElement<{ "aria-describedby"?: string }>(child)
          ? cloneElement(child, { "aria-describedby": [child.props["aria-describedby"], hintId].filter(Boolean).join(" ") })
          : child) : children}
      {hint && <p id={hintId} className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SegButton({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "min-h-9 rounded-full border px-4 text-sm font-medium transition-colors",
        pressed ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

export function DeployWizardPage({ presetId, invalidPreset = false, presetHotSwitch, presetClusterId }: { presetId?: string; invalidPreset?: boolean; presetHotSwitch?: boolean; presetClusterId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProjects, availableProjects } = useProjects();
  const admin = isAdmin();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => { const base = initialForm(MODEL_CATALOG[0]); return { ...base, clusterId: presetClusterId || base.clusterId, engine: { ...base.engine, hotSwitch: !!presetHotSwitch } }; });
  const [customPath, setCustomPath] = useState("");
  const [busy, setBusy] = useState(false);
  const deploymentGuard = useDeploymentGuard();
  const [error, setError] = useState("");
  const [recipeName, setRecipeName] = useState("");
  const [recipeDescription, setRecipeDescription] = useState("");
  const [loadedPreset, setLoadedPreset] = useState<string>();
  const [presetError, setPresetError] = useState(false);
  const [selectionInvalid, setSelectionInvalid] = useState(false);
  const appliedPreset = useRef<string>();
  const singleProject =
    selectedProjects?.length === 1 && selectedProjects[0] !== "all" ? selectedProjects[0] : "";
  const [catalog, setCatalog] = useState({ publish: true, projectId: singleProject, name: "", isPublic: false });
  const clusters = useQuery({ queryKey: omnimizerKeys.clusters, queryFn: omnimizerApi.listClusters });
  const predefined = useQuery({ queryKey: omnimizerKeys.predefined, queryFn: omnimizerApi.listPredefined, retry: false });
  useEffect(() => {
    if (!presetId) { appliedPreset.current = undefined; setPresetError(false); return; }
    if (!predefined.data || appliedPreset.current === presetId) return;
    appliedPreset.current = presetId;
    try {
      const recipe = predefined.data.find((p) => String(p.id) === presetId);
      if (!recipe) throw new Error("missingPreset");
      const next = formFromPreset(recipe);
      setForm({ ...next, clusterId: presetClusterId || next.clusterId, engine: { ...next.engine, hotSwitch: !!presetHotSwitch || next.engine.hotSwitch } });
      setCustomPath(next.model.source === "custom" ? next.model.hfPath : "");
      setStep(0);
      setPresetError(false);
    } catch { setPresetError(true); }
    setLoadedPreset(presetId);
  }, [presetId, predefined.data, presetClusterId, presetHotSwitch]);
  const organizationModels: CatalogModel[] = (predefined.data || [])
    .filter((p) => p.model_source === "hf" && p.is_active !== false)
    .map((p) => {
      const known = MODEL_CATALOG.find((m) => m.hfPath === p.model_url);
      return {
        ...(known || customModel(p.model_url)),
        id: `org-${p.id ?? p.name}`,
        label: p.name,
        description: p.description || known?.description || p.model_url,
        source: "organization" as const,
      };
    });
  const cluster = clusters.data?.find((c) => c.id === form.clusterId);
  const clusterUnavailable = Boolean(form.clusterId && clusters.data && !cluster);
  const gpuTypes = cluster?.supported_gpu_types?.length ? cluster.supported_gpu_types : Object.keys(GPU_CATALOG);
  const sizing = useMemo(() => estimateSizing(form), [form]);
  const payload = buildDeploymentPayload(form);
  const engine = form.engine;
  const setEngine = (patch: Partial<typeof engine>) => setForm({ ...form, engine: { ...engine, ...patch } });
  const selectModel = (model: CatalogModel) => {
    setSelectionInvalid(false);
    setError("");
    setForm({
      ...form,
      model,
      name: form.name.startsWith(slug(form.model.id)) ? initialForm(model).name : form.name,
      ...resourcesFor(model),
      engine: { ...defaultEngine(model), hotSwitch: form.engine.hotSwitch },
      llmExtras: undefined,
      dynamoExtras: undefined,
    });
  };
  const selectRecipe = (recipe: PredefinedDeployment) => {
    try {
      const next = formFromPreset(recipe);
      setForm({ ...next, clusterId: presetClusterId || next.clusterId, engine: { ...next.engine, hotSwitch: form.engine.hotSwitch || next.engine.hotSwitch } });
      setCustomPath(""); setSelectionInvalid(false); setError("");
    } catch { setSelectionInvalid(true); setError(t("omnimizer.wizard.presetInvalid")); }
  };
  const catalogInvalid = catalog.publish && !validCatalogTarget(catalog, availableProjects || [], admin);
  const blocked = sizing.verdict === "too-large";
  const validRepository = /^[\w.-]+\/[\w.-]+$/.test(form.model.hfPath);
  const canContinue = validRepository && !busy && !selectionInvalid && (step === 0 || !clusterUnavailable) && (step !== 3 || (!blocked && !catalogInvalid));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canContinue) return;
    if (step < 3) {
      if (canContinue) setStep(step + 1);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const created = await deploymentGuard.run(payload, () => omnimizerApi.createDeployment(payload));
      if (!created) return;
      if (catalog.publish)
        saveCatalogIntent(created.model_id, {
          projectId: catalog.isPublic ? "" : catalog.projectId,
          name: catalog.name.trim() || form.name,
          isPublic: catalog.isPublic,
        });
      await queryClient.invalidateQueries({ queryKey: omnimizerKeys.deployments });
      toast.success(
        catalog.publish ? t("omnimizer.wizard.createdWithCatalog", { name: form.name }) : t("omnimizer.wizard.created", { name: form.name }),
      );
      void navigate({ to: "/$", params: { _splat: "deployments" } });
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail ? JSON.stringify(detail) : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveRecipe() {
    if (!admin || busy) return;
    setBusy(true); setError("");
    try {
      await omnimizerApi.createPredefined(recipePayload(form, recipeName.trim() || form.name, recipeDescription));
      await queryClient.invalidateQueries({ queryKey: omnimizerKeys.predefined });
      toast.success(t("omnimizer.wizard.recipeSaved"));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("omnimizer.common.loadFailed"));
    } finally { setBusy(false); }
  }
  if (invalidPreset || (presetId && (presetError || predefined.error))) return (
    <div role="alert" className="flex flex-col items-start gap-3 rounded-lg border border-destructive p-4 text-foreground">
      <p>{t("omnimizer.wizard.presetInvalid")}</p>
      <LegacyLink to="/create-deployment" className="underline">{t("omnimizer.wizard.advancedLink")}</LegacyLink>
      <Button variant="outline" onClick={() => void navigate({ to: "/deployments/new", search: {} })}>{t("omnimizer.wizard.clearPreset")}</Button>
    </div>
  );
  if (presetId && loadedPreset !== presetId) return <p role="status">{t("omnimizer.common.loading")}</p>;
  return (
    <div className="flex flex-col gap-4">
      {deploymentGuard.dialogs}
      <ol className="flex flex-wrap gap-2" aria-label={t("omnimizer.wizard.stepsLabel")}>
        {STEPS.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              disabled={i > step}
              onClick={() => i < step && setStep(i)}
              aria-current={i === step ? "step" : undefined}
              className={cn(
                "flex min-h-10 items-center gap-2 rounded-full border py-1.5 pe-4 ps-1.5 text-sm",
                i === step ? "border-primary font-semibold text-foreground" : "border-border text-muted-foreground",
              )}
            >
              <span className={cn("grid size-7 place-items-center rounded-full text-xs font-semibold", i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-primary/15 text-primary" : "bg-muted")}>
                {i < step ? <Check className="size-3.5" aria-hidden /> : i + 1}
              </span>
              {t(`omnimizer.wizard.steps.${s}`)}
            </button>
          </li>
        ))}
      </ol>
      <form onSubmit={submit} className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card className="flex min-w-0 flex-col gap-5 p-5">
          {step === 0 && (
            <>
              <header>
                <h2 className="text-lg font-semibold">{t("omnimizer.wizard.modelTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("omnimizer.wizard.modelSubtitle")}</p>
              </header>
              {organizationModels.length > 0 && (
                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold">{t("omnimizer.wizard.orgCatalog")}</h3>
                  <ModelGrid models={organizationModels} selected={form.model} onSelect={(model) => {
                    const recipe = predefined.data?.find((p) => `org-${p.id ?? p.name}` === model.id);
                    if (recipe) selectRecipe(recipe);
                  }} />
                </section>
              )}
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">{t("omnimizer.wizard.omnimizerCatalog")}</h3>
                <ModelGrid models={MODEL_CATALOG} selected={form.model} onSelect={selectModel} />
              </section>
              <section className={cn("flex flex-col gap-2 rounded-lg border p-3", form.model.source === "custom" ? "border-primary ring-2 ring-primary/30" : "border-transparent")}>
                <h3 className="text-sm font-semibold">{t("omnimizer.wizard.otherModel")}</h3>
                <Label htmlFor="wizard-hf-repository">{t("omnimizer.wizard.hfRepository")}</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    id="wizard-hf-repository" aria-describedby="wizard-hf-repository-hint"
                    placeholder="organization/model-name"
                    value={customPath}
                    onChange={(e) => {
                      setCustomPath(e.target.value);
                      if (form.model.source === "custom") selectModel(customModel(e.target.value.trim()));
                    }}
                  />
                  <Button
                    type="button"
                    variant={form.model.source === "custom" ? "default" : "outline"}
                    aria-pressed={form.model.source === "custom"}
                    disabled={!/^[^/\s]+\/[^/\s]+$/.test(customPath.trim())}
                    onClick={() => selectModel(customModel(customPath.trim()))}
                  >
                    {t("omnimizer.wizard.useModel")}
                  </Button>
                </div>
                <p id="wizard-hf-repository-hint" className="text-sm text-muted-foreground">{t("omnimizer.wizard.hfRepositoryHint")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("omnimizer.wizard.advancedHint")}{" "}
                  <LegacyLink to="/create-deployment" className="text-primary underline-offset-4 hover:underline">
                    {t("omnimizer.wizard.advancedLink")}
                  </LegacyLink>
                </p>
              </section>
            </>
          )}
          {step === 1 && (
            <>
              <header>
                <h2 className="text-lg font-semibold">{t("omnimizer.wizard.gpuTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("omnimizer.wizard.gpuSubtitle")}</p>
              </header>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("omnimizer.wizard.cluster")} htmlFor="wizard-cluster" hint={t("omnimizer.wizard.guidance.cluster.helper")} tooltip={t("omnimizer.wizard.guidance.cluster.tooltip")}>
                  <select
                    id="wizard-cluster"
                    className={fieldClass}
                    value={form.clusterId}
                    onChange={(e) => {
                      const c = clusters.data?.find((c) => c.id === e.target.value);
                      setForm({
                        ...form,
                        clusterId: e.target.value,
                        namespace: c?.available_namespaces?.[0] || form.namespace,
                        gpuType: c?.supported_gpu_types?.[0] || form.gpuType,
                      });
                    }}
                  >
                    <option value="">{t("omnimizer.wizard.autoCluster")}</option>
                    {clusterUnavailable && <option value={form.clusterId} disabled>{t("omnimizer.wizard.clusterUnavailable", { id: form.clusterId })}</option>}
                    {(clusters.data || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name} · {c.status}</option>
                    ))}
                  </select>
                  {clusterUnavailable && <p role="alert" className="text-sm text-muted-foreground">{t("omnimizer.wizard.clusterUnavailable", { id: form.clusterId })}</p>}
                </Field>
                <Field label={t("omnimizer.wizard.namespace")} htmlFor="wizard-namespace" hint={t("omnimizer.wizard.guidance.namespace.helper")} tooltip={t("omnimizer.wizard.guidance.namespace.tooltip")}>
                  <Input id="wizard-namespace" required value={form.namespace} onChange={(e) => setForm({ ...form, namespace: e.target.value })} />
                </Field>
              </div>
              <Field label={t("omnimizer.wizard.gpuType")} hint={t("omnimizer.wizard.guidance.gpuType.helper")} tooltip={t("omnimizer.wizard.guidance.gpuType.tooltip")}>
              <div className="grid min-w-0 gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(min(130px,100%),1fr))]" role="radiogroup" aria-label={t("omnimizer.wizard.gpuType")}>
                {gpuTypes.map((type) => {
                  const spec = gpuSpec(type);
                  const selected = form.gpuType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setForm({ ...form, gpuType: type })}
                      className={cn(
                        "flex flex-col items-start gap-1 rounded-xl border bg-muted/30 p-3 text-start transition-colors hover:border-ring",
                        selected ? "border-primary ring-2 ring-primary/30" : "border-border",
                      )}
                    >
                      <Cpu className="size-4 text-primary" aria-hidden />
                      <strong className="text-sm">{spec?.label || type}</strong>
                      <span className="text-xs text-muted-foreground">{spec ? `${spec.memoryGb} GB` : t("omnimizer.wizard.unknownMemory")}</span>
                    </button>
                  );
                })}
              </div>
              </Field>
              <Field label={t("omnimizer.wizard.gpusPerWorker")} hint={t("omnimizer.wizard.guidance.gpusPerWorker.helper")} tooltip={t("omnimizer.wizard.guidance.gpusPerWorker.tooltip")}>
                <div role="group" aria-label={t("omnimizer.wizard.gpusPerWorker")} className="flex flex-wrap gap-1.5">
                  {GPU_COUNTS.filter((n) => !cluster?.max_gpu_count || n <= cluster.max_gpu_count).map((n) => (
                    <SegButton key={n} pressed={form.gpuCount === n} onClick={() => setForm({ ...form, gpuCount: n })}>{n}</SegButton>
                  ))}
                </div>
              </Field>
              <details className="border-t border-border pt-4">
                <summary className="cursor-pointer text-sm font-medium text-primary">{t("omnimizer.wizard.resources")}</summary>
                <div className="mt-4 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
                  {(["cpuRequest", "cpuLimit", "memoryRequest", "memoryLimit", "storage"] as const).map((key) => (
                    <Field key={key} label={t(`omnimizer.wizard.resourceFields.${key}`)} htmlFor={`wizard-${key}`} hint={t(`omnimizer.wizard.guidance.${key}.helper`)} tooltip={t(`omnimizer.wizard.guidance.${key}.tooltip`)}>
                      <Input id={`wizard-${key}`} required value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                    </Field>
                  ))}
                  <Field label={t("omnimizer.wizard.routing")} htmlFor="wizard-routing" hint={t("omnimizer.wizard.guidance.routing.helper")} tooltip={t("omnimizer.wizard.guidance.routing.tooltip")}>
                    <select id="wizard-routing" className={fieldClass} value={form.routing} onChange={(e) => setForm({ ...form, routing: e.target.value as DeploymentForm["routing"] })}>
                      <option value="round-robin">{t("omnimizer.wizard.routingRoundRobin")}</option>
                      <option value="kv">{t("omnimizer.wizard.routingKv")}</option>
                    </select>
                  </Field>
                </div>
              </details>
            </>
          )}
          {step === 2 && (
            <>
              <header>
                <h2 className="text-lg font-semibold">{t("omnimizer.wizard.engineTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("omnimizer.wizard.engineSubtitle")}</p>
              </header>
              <Field
                label={t("omnimizer.wizard.context")}
                htmlFor="wizard-context"
                hint={form.model.maxContext ? t("omnimizer.wizard.contextLimit", { value: form.model.maxContext.toLocaleString() }) : t("omnimizer.wizard.contextHint")}
              >
                <Input id="wizard-context" type="number" min={128} required value={engine.maxModelLen} onChange={(e) => setEngine({ maxModelLen: Number(e.target.value) })} />
                <div className="flex flex-wrap gap-1.5">
                  {CONTEXT_PRESETS.filter((n) => !form.model.maxContext || n <= form.model.maxContext).map((n) => (
                    <SegButton key={n} pressed={engine.maxModelLen === n} onClick={() => setEngine({ maxModelLen: n })}>{`${n / 1024}k`}</SegButton>
                  ))}
                </div>
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("omnimizer.wizard.concurrency")} htmlFor="wizard-seqs" hint={t("omnimizer.wizard.concurrencyHint")}>
                  <Input id="wizard-seqs" type="number" min={1} required value={engine.maxNumSeqs} onChange={(e) => setEngine({ maxNumSeqs: Number(e.target.value) })} />
                </Field>
                <Field
                  label={t("omnimizer.wizard.toolCalling")}
                  htmlFor="wizard-tools"
                  hint={form.model.toolCallParser ? t("omnimizer.wizard.recommended", { value: form.model.toolCallParser }) : t("omnimizer.wizard.toolCallingHint")}
                >
                  <select id="wizard-tools" className={fieldClass} value={engine.toolCallParser} onChange={(e) => setEngine({ toolCallParser: e.target.value })}>
                    <option value="">{t("omnimizer.common.disabled")}</option>
                    {TOOL_CALL_PARSERS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field
                  label={t("omnimizer.wizard.reasoning")}
                  htmlFor="wizard-reasoning"
                  hint={form.model.reasoningParser ? t("omnimizer.wizard.recommended", { value: form.model.reasoningParser }) : t("omnimizer.wizard.reasoningHint")}
                >
                  <select id="wizard-reasoning" className={fieldClass} value={engine.reasoningParser} onChange={(e) => setEngine({ reasoningParser: e.target.value })}>
                    <option value="">{t("omnimizer.common.disabled")}</option>
                    {REASONING_PARSERS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
              </div>
              <div className="relative flex items-start gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4 pe-10">
                <Switch
                  id="wizard-hot-switch"
                  checked={engine.hotSwitch}
                  onCheckedChange={(v: boolean) => setEngine({ hotSwitch: v, ...(v ? { kvOffloadEnabled: false } : {}) })}
                />
                <div className="flex flex-1 flex-col gap-1">
                  <label htmlFor="wizard-hot-switch" className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                    {t("omnimizer.wizard.hotSwitch")}
                    <Badge variant="outline" className="text-[11px]">{t("omnimizer.wizard.hotSwitchBadge")}</Badge>
                  </label>
                  <InfoTip label={t("omnimizer.wizard.hotSwitch")} className="absolute end-3 top-3">{t("omnimizer.fastSwitch.info.sleep")}</InfoTip>
                  <p className="text-xs text-muted-foreground">{t("omnimizer.wizard.hotSwitchHint")}</p>
                  {engine.hotSwitch && (
                    <p className="text-xs text-muted-foreground">
                      {t("omnimizer.wizard.hotSwitchRequirements", { percent: Math.round(HOT_SWITCH_GPU_MEMORY_UTILIZATION * 100) })}
                    </p>
                  )}
                </div>
              </div>
              <div className="relative flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-4 pe-10">
                <InfoTip label={t("omnimizer.wizard.fastLoad")} className="absolute end-3 top-3">{t("omnimizer.fastSwitch.info.fastLoad")}</InfoTip>
                <Switch id="wizard-fast-load" checked={engine.fastLoad} onCheckedChange={(v: boolean) => setEngine({ fastLoad: v })} />
                <div className="flex flex-1 flex-col gap-1">
                  <label htmlFor="wizard-fast-load" className="text-sm font-semibold">{t("omnimizer.wizard.fastLoad")}</label>
                  <p className="text-xs text-muted-foreground">{t("omnimizer.wizard.fastLoadHint")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-4">
                <Switch
                  id="wizard-offload"
                  checked={engine.kvOffloadEnabled}
                  disabled={engine.hotSwitch}
                  onCheckedChange={(v: boolean) => setEngine({ kvOffloadEnabled: v })}
                />
                <div className="flex flex-1 flex-col gap-2">
                  <label htmlFor="wizard-offload" className="text-sm font-semibold">{t("omnimizer.wizard.offload")}</label>
                  <p className="text-xs text-muted-foreground">
                    {engine.hotSwitch ? t("omnimizer.wizard.offloadHotSwitch") : t("omnimizer.wizard.offloadHint")}
                  </p>
                  {engine.kvOffloadEnabled && (
                    <Field label={t("omnimizer.wizard.offloadRam")} htmlFor="wizard-offload-ram" hint={t("omnimizer.wizard.offloadRamHint")}>
                      <Input id="wizard-offload-ram" type="number" min={1} max={1048576} step="any" required value={engine.kvOffloadRamGib} onChange={(e) => setEngine({ kvOffloadRamGib: Number(e.target.value) })} />
                    </Field>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("omnimizer.wizard.precisionNote")}
                {form.model.prequantized ? ` (${form.model.prequantized.toUpperCase()})` : ""}
              </p>
            </>
          )}
          {step === 3 && (
            <>
              <header>
                <h2 className="text-lg font-semibold">{t("omnimizer.wizard.reviewTitle")}</h2>
              </header>
              <Field label={t("omnimizer.wizard.deploymentName")} htmlFor="wizard-name">
                <Input id="wizard-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                {[
                  ["model", `${form.model.label} · ${form.model.hfPath}`],
                  ["gpus", `${form.gpuCount} × ${gpuSpec(form.gpuType)?.label || form.gpuType}`],
                  ["cluster", `${cluster?.name || t("omnimizer.wizard.autoSelected")} · ${form.namespace}`],
                  ["context", t("omnimizer.wizard.contextSummary", { tokens: engine.maxModelLen.toLocaleString(), seqs: engine.maxNumSeqs })],
                  ["tools", engine.toolCallParser || t("omnimizer.common.disabled")],
                  ["reasoning", engine.reasoningParser || t("omnimizer.common.disabled")],
                  ["offload", engine.kvOffloadEnabled ? t("omnimizer.wizard.offloadSummary", { value: engine.kvOffloadRamGib }) : t("omnimizer.common.disabled")],
                  ["hotSwitch", engine.hotSwitch ? t("omnimizer.common.enabled") : t("omnimizer.common.disabled")],
                  ["fastLoad", engine.fastLoad ? t("omnimizer.common.enabled") : t("omnimizer.common.disabled")],
                ].map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-muted-foreground">{t(`omnimizer.wizard.summary.${k}`)}</dt>
                    <dd className="break-words">{v}</dd>
                  </div>
                ))}
              </dl>
              <section className="flex flex-col gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <Switch id="wizard-catalog" checked={catalog.publish} onCheckedChange={(v: boolean) => setCatalog({ ...catalog, publish: v })} />
                  <div className="flex-1">
                    <label htmlFor="wizard-catalog" className="flex items-center gap-2 text-sm font-semibold">
                      <Sparkle className="size-4 text-primary" aria-hidden />
                      {t("omnimizer.wizard.catalogTitle")}
                    </label>
                    <p className="text-xs text-muted-foreground">{t("omnimizer.wizard.catalogHint")}</p>
                  </div>
                </div>
                {catalog.publish && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t("omnimizer.wizard.catalogName")} htmlFor="wizard-catalog-name">
                      <Input id="wizard-catalog-name" placeholder={form.name} value={catalog.name} onChange={(e) => setCatalog({ ...catalog, name: e.target.value })} />
                    </Field>
                    <CatalogTargetFields prefix="wizard-catalog" value={catalog} onChange={(target) => setCatalog({ ...catalog, ...target })} projects={availableProjects || []} admin={admin} />
                  </div>
                )}
              </section>
              {admin && <section className="flex flex-col gap-3 rounded-xl border border-border p-4">
                <Field label={t("omnimizer.wizard.recipeName")} htmlFor="wizard-recipe-name">
                  <Input id="wizard-recipe-name" value={recipeName} placeholder={form.name} onChange={(event) => setRecipeName(event.target.value)} />
                </Field>
                <Field label={t("omnimizer.wizard.recipeDescription")} htmlFor="wizard-recipe-description">
                  <Textarea id="wizard-recipe-description" value={recipeDescription} onChange={(event) => setRecipeDescription(event.target.value)} />
                </Field>
                <Button type="button" variant="outline" disabled={busy} onClick={() => void saveRecipe()}>{t("omnimizer.wizard.saveRecipe")}</Button>
              </section>}
              <details className="border-t border-border pt-4">
                <summary className="cursor-pointer text-sm font-medium text-primary">{t("omnimizer.wizard.requestJson")}</summary>
                <pre className="mt-3 max-h-80 overflow-auto rounded-lg border border-border bg-muted/30 p-3 text-xs">{JSON.stringify(payload, null, 2)}</pre>
              </details>
              {error && (
                <p role="alert" className="whitespace-pre-wrap rounded-lg border border-destructive bg-[var(--destructive-100)] p-3 text-sm text-foreground">{error}</p>
              )}
            </>
          )}
          <div className="flex justify-between border-t border-border pt-4">
            <Button type="button" variant="outline" disabled={step === 0 || busy} onClick={() => setStep(step - 1)}>
              <ArrowLeft aria-hidden className="rtl:-scale-x-100" />
              {t("omnimizer.wizard.back")}
            </Button>
            <Button type="submit" disabled={!canContinue}>
              {step < 3 ? (
                <>
                  {t("omnimizer.wizard.continue")}
                  <ArrowRight aria-hidden className="rtl:-scale-x-100" />
                </>
              ) : busy ? (
                <>
                  <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden />
                  {t("omnimizer.wizard.deploying")}
                </>
              ) : (
                <>
                  <Rocket aria-hidden />
                  {t("omnimizer.wizard.deploy")}
                </>
              )}
            </Button>
          </div>
        </Card>
        <SizingPanel form={form} sizing={sizing} />
      </form>
    </div>
  );
}

function ModelGrid({ models, selected, onSelect }: { models: CatalogModel[]; selected: CatalogModel; onSelect: (m: CatalogModel) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid min-w-0 gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(100%,230px),1fr))]" role="radiogroup">
      {models.map((m) => {
        const active = selected.id === m.id;
        return (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onSelect(m)}
            className={cn(
              "flex flex-col items-start gap-1.5 rounded-xl border bg-muted/30 p-3.5 text-start transition-colors hover:border-ring",
              active ? "border-primary ring-2 ring-primary/30" : "border-border",
            )}
          >
            <span className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
              {m.label}
              {m.prequantized && <Badge variant="muted">{m.prequantized.toUpperCase()}</Badge>}
              {(m.toolCallParser || m.reasoningParser) && (
                <Badge variant="success" className="gap-1">
                  <Sparkles className="size-3" aria-hidden />
                  {m.reasoningParser ? t("omnimizer.wizard.reasoningBadge") : t("omnimizer.wizard.toolsBadge")}
                </Badge>
              )}
            </span>
            <span className="text-xs text-muted-foreground">
              {m.source === "omnimizer" ? t(`omnimizer.models.${m.id.replace(/\./g, "_")}`, { defaultValue: m.description }) : m.description}
            </span>
            <span className="text-xs font-medium text-primary tabular-nums">
              {m.parameters} · {m.weightsGb ? `${m.weightsGb} GB` : t("omnimizer.wizard.sizeUnknown")}
              {m.maxContext ? ` · ${Math.round(m.maxContext / 1024)}k ctx` : ""}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SizingPanel({ form, sizing }: { form: DeploymentForm; sizing: Sizing }) {
  const { t } = useTranslation();
  const gb = (v: number) => `${v.toFixed(v < 10 ? 1 : 0)} GB`;
  const free = Math.max(0, sizing.budgetGb - sizing.weightsGb - sizing.overheadGb);
  const pct = (v: number) => `${Math.min(100, (v / Math.max(sizing.budgetGb, 0.001)) * 100)}%`;
  const tone = {
    fits: "success",
    tight: "muted",
    "too-large": "destructive",
    unknown: "outline",
  } as const;
  return (
    <aside className="lg:sticky lg:top-4">
      <Card className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">{t("omnimizer.sizing.title")}</h2>
          <Badge variant={tone[sizing.verdict]}>{t(`omnimizer.sizing.verdict.${sizing.verdict}`)}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {form.model.label} · {form.gpuCount} × {gpuSpec(form.gpuType)?.label || form.gpuType}
        </p>
        {sizing.available ? (
          <>
            <div className="flex h-3 overflow-hidden rounded-full bg-muted" role="img" aria-label={t("omnimizer.sizing.barLabel")}>
              <span className="h-full bg-chart-2" style={{ width: pct(sizing.weightsGb) }} />
              <span className="h-full bg-muted-foreground/50" style={{ width: pct(sizing.overheadGb) }} />
              <span className="h-full bg-primary" style={{ width: pct(Math.min(sizing.kvRequestedGb, free)) }} />
              {sizing.kvRequestedGb > free && <span className="h-full w-[4%] bg-[var(--warning)]" />}
            </div>
            <ul className="flex flex-col gap-1.5 text-sm">
              {[
                ["bg-chart-2", t("omnimizer.sizing.weights"), sizing.weightsGb],
                ["bg-muted-foreground/50", t("omnimizer.sizing.overhead"), sizing.overheadGb],
                ["bg-primary", t("omnimizer.sizing.kv", { seqs: form.engine.maxNumSeqs }), sizing.kvRequestedGb],
                ...(sizing.kvRequestedGb > free ? [["bg-[var(--warning)]", t("omnimizer.sizing.overflow"), sizing.kvRequestedGb - free] as const] : []),
                ["border border-border bg-muted", t("omnimizer.sizing.budget"), sizing.budgetGb],
              ].map(([color, label, value]) => (
                <li key={label as string} className="flex items-center gap-2 text-muted-foreground">
                  <span className={cn("size-2.5 shrink-0 rounded-sm", color as string)} aria-hidden />
                  {label as string}
                  <b className="ms-auto font-semibold text-foreground tabular-nums">{gb(value as number)}</b>
                </li>
              ))}
            </ul>
            <p className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-foreground">
              <strong className="me-1 text-lg tabular-nums">{sizing.concurrentFullContext}</strong>
              {t("omnimizer.sizing.concurrent")}
            </p>
            {form.engine.kvOffloadEnabled && (
              <p className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-foreground">
                <strong className="me-1 text-lg tabular-nums">+{sizing.offloadedFullContext}</strong>
                {t("omnimizer.sizing.offloaded")}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("omnimizer.sizing.unknown")}</p>
        )}
        {sizing.warnings.map((w) => (
          <p key={w.key} className="rounded-lg border border-[var(--warning)] bg-[var(--warning-100)] p-2.5 text-xs text-foreground">
            {t(`omnimizer.sizing.warnings.${w.key}`, w.values)}
          </p>
        ))}
        <p className="text-xs text-muted-foreground">{t("omnimizer.sizing.disclaimer")}</p>
      </Card>
    </aside>
  );
}
